import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { Toaster } from "@/components/ui/sonner";
import { TenantMetadata } from "@/components/TenantMetadata";
import { AuthProvider, useAuth, type Role } from "@/contexts/AuthContext";
import { SignInReturn } from "@/components/auth/SignInReturn";
import { ProtectedRoute } from "@/components/shell/ProtectedRoute";
import { AppShell } from "@/components/shell/AppShell";
import { RegistrarWorkspaceFrame } from "@/components/registrar/RegistrarWorkspaceFrame";
import { RouteSkeleton } from "@/components/shell/RouteSkeleton";
import { ScrollToTop } from "@/components/shell/ScrollToTop";
import { SignInPage } from "@/pages/SignInPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { ActivatePage } from "@/pages/ActivatePage";
import { AccessPendingPage } from "@/pages/AccessPendingPage";
import { WorkspaceUnavailablePage } from "@/pages/WorkspaceUnavailablePage";
import { ApplyPage } from "@/pages/ApplyPage";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicyPage";
import { TermsPage } from "@/pages/TermsPage";
import { LandingPage } from "@/pages/LandingPage";
import { LEGAL_ROUTES } from "@/content/legal";
import { InvitationsPage } from "@/pages/admin/InvitationsPage";
import { InvitationRecordPage } from "@/pages/admin/InvitationRecordPage";
import { PeoplePage } from "@/pages/admin/PeoplePage";
import { PersonRecordPage } from "@/pages/admin/PersonRecordPage";
import { LegacyFacultyLookup } from "@/pages/admin/LegacyFacultyLookup";
import { AdmissionsPage } from "@/pages/admin/AdmissionsPage";
import { ApplicantFilePage } from "@/pages/admin/ApplicantFilePage";
import { AcademicStructurePage } from "@/pages/admin/AcademicStructurePage";
import { ImportsPage } from "@/pages/admin/ImportsPage";
import { CourseHistoryPage } from "@/pages/admin/CourseHistoryPage";
import { ClassroomIntakePage } from "@/pages/admin/ClassroomIntakePage";
import { AdminDashboardPage } from "@/pages/admin/AdminDashboardPage";
import { SettingsPage } from "@/pages/admin/SettingsPage";
import { IntegrationsPage } from "@/pages/admin/IntegrationsPage";
import { CourseDrivePage } from "@/pages/CourseDrivePage";
import { InstructorDashboardPage } from "@/pages/instructor/InstructorDashboardPage";
import { ProfilePage } from "@/pages/student/ProfilePage";
import { StudentDashboardPage } from "@/pages/student/StudentDashboardPage";
import { SessionListPage } from "@/pages/session/SessionListPage";
import { SessionPortalPage } from "@/pages/session/SessionPortalPage";
import { GoogleMeetOAuthCallbackPage } from "@/pages/session/GoogleMeetOAuthCallbackPage";
import { GoogleClassroomOAuthCallbackPage } from "@/pages/courses/GoogleClassroomOAuthCallbackPage";
import { InboxPage } from "@/pages/InboxPage";
import { AttendancePage } from "@/pages/AttendancePage";
import { CoursesPage } from "@/pages/CoursesPage";
import { RegistrarCoursePage } from "@/pages/admin/RegistrarCoursePage";
import { FacultyPage } from "@/pages/FacultyPage";
import { GradesPage as StudentGradesPage } from "@/pages/student/GradesPage";
import {
  ACCESS_PENDING_ROUTE,
  LEGACY_PROTECTED_ROUTES,
  PROTECTED_ROUTES,
  WORKSPACE_UNAVAILABLE_ROUTE,
  seatRecordDestination,
  type CourseWorkspaceShell,
} from "@/navigation/routes";
import { DesignSystemPage } from "@/pages/DesignSystemPage";
import { tenantSlugFromHostname } from "@/lib/tenant";
import { homeForMember, needsAccessResolution } from "@/navigation/member-routing";
import { SeatRecordEntry } from "@/navigation/SeatRecordEntry";
import { RegistrarRecordEntry } from "@/navigation/RegistrarRecordEntry";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

/** Authenticated → their role's landing; logged-out → the public marketing page. */
function RootEntry() {
  const { isAuthenticated, isLoading, me, membershipStatus, role } = useAuth();
  if (isLoading) return <RouteSkeleton />;
  if (isAuthenticated) return <SignInReturn fallback={homeForMember(role, me?.workspaces)} />;
  // A session with no membership would otherwise read as a logged-out visitor
  // and be shown the marketing page, which is the same silent dead end the
  // sign-in redirect was.
  if (needsAccessResolution(membershipStatus)) {
    return <Navigate to={ACCESS_PENDING_ROUTE} replace />;
  }
  return <LandingPage />;
}

function TenantUnavailable() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <section className="max-w-md text-center" aria-labelledby="tenant-unavailable-title">
        <h1 id="tenant-unavailable-title" className="text-title font-semibold">
          Learning portal unavailable
        </h1>
        <p className="mt-3 text-ui text-muted-foreground">
          This web address is not configured for a learning portal.
        </p>
      </section>
    </main>
  );
}

/** One authenticated shell: the role gate and the rail around the routed page. */
function Shell({ roles, workspace }: { roles?: Role[]; workspace?: "teaching" | "learning" }) {
  return (
    <ProtectedRoute roles={roles} workspace={workspace}>
      <AppShell><Outlet /></AppShell>
    </ProtectedRoute>
  );
}

/**
 * The seat course list is retired: the Home's course collection lists every
 * course. Old `?course=` links still open their course, and the bare path
 * lands on the Home.
 */
function SeatCoursesEntry({ shell }: { shell: CourseWorkspaceShell }) {
  const location = useLocation();
  return (
    <Navigate
      to={seatRecordDestination(shell, location.pathname, location.search, location.hash)
        ?? PROTECTED_ROUTES[shell].dashboard}
      replace
    />
  );
}

function CourseWorkspaceEntry({
  shell,
  workspace,
}: {
  shell: CourseWorkspaceShell;
  workspace: "role" | "teaching" | "learning";
}) {
  const { courseId } = useParams();
  return courseId
    ? <CoursesPage workspace={workspace} courseId={courseId} shell={shell} />
    : <Navigate to={PROTECTED_ROUTES[shell].dashboard} replace />;
}

if (import.meta.env.DEV) void import("../previews/dither-palettes");

const OriginalAsciiRevealDemo = import.meta.env.DEV ? lazy(() => import("../previews/ascii-reveal/OriginalAsciiRevealDemo")) : null;
const AsciiRevealStudio = import.meta.env.DEV ? lazy(() => import("../previews/ascii-reveal-v2/AsciiRevealStudio")) : null;

const App = () => {
  if (AsciiRevealStudio && window.location.pathname === "/dev/ascii-reveal-v2") {
    return <Suspense fallback={null}><AsciiRevealStudio /></Suspense>;
  }
  if (OriginalAsciiRevealDemo && window.location.pathname === "/dev/ascii-reveal") {
    return <Suspense fallback={null}><OriginalAsciiRevealDemo /></Suspense>;
  }
  if (!tenantSlugFromHostname()) {
    return <TenantUnavailable />;
  }

  return (
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TenantMetadata />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Internal maintenance surface. Intentionally absent from role navigation. */}
          <Route path="/design-system" element={<DesignSystemPage />} />
          {/* Public auth surface */}
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Public admissions intake (issue #9) — no account, no shell. */}
          <Route path="/apply" element={<ApplyPage />} />

          {/* Account activation from an invitation (issue #78). Public for the
              same reason /apply is: the person has no account yet. */}
          <Route path="/activate" element={<ActivatePage />} />
          {/* Opens only in the short-lived Google OAuth popup. It forwards the
              code and signed state to its opener and stores neither. */}
          <Route path="/google-meet/callback" element={<GoogleMeetOAuthCallbackPage />} />
          <Route path="/google-classroom/callback" element={<GoogleClassroomOAuthCallbackPage />} />

          {/* A valid session that carries no LMS authority yet. Outside the role
              gate on purpose: everyone who belongs here fails that gate. */}
          <Route path={ACCESS_PENDING_ROUTE} element={<AccessPendingPage />} />
          <Route path={WORKSPACE_UNAVAILABLE_ROUTE} element={<WorkspaceUnavailablePage />} />

          {/* Public legal documents. Unauthenticated for two reasons: Google
              requires a reachable Privacy Policy URI before an External OAuth
              app is published, and a prospective student must be able to read
              both before deciding to apply. Both render static content and make
              no request, so neither needs a session or a tenant header. */}
          <Route path={LEGAL_ROUTES.privacy} element={<PrivacyPolicyPage />} />
          <Route path={LEGAL_ROUTES.terms} element={<TermsPage />} />

          {/* The registrar shell. One address per destination: a course, a
              person, an applicant file, and an invitation are each the path,
              and a course's or a person's views are `?view=`
              (`registrarCoursePath`, `registrarPersonPath`). Retired registrar
              addresses stay routable only as compatibility redirects, inside
              the admin gate so a non-admin never learns the destination:
              RegistrarRecordEntry resolves each arrival once through
              registrarRecordDestination. The Faculty lookup renders while it
              resolves, so it sits inside the frame. */}
          <Route element={<Shell roles={["admin"]} />}>
            <Route element={<RegistrarWorkspaceFrame><Outlet /></RegistrarWorkspaceFrame>}>
              <Route path={PROTECTED_ROUTES.admin.dashboard} element={<AdminDashboardPage />} />
              <Route path={PROTECTED_ROUTES.admin.admissions} element={<RegistrarRecordEntry><AdmissionsPage /></RegistrarRecordEntry>} />
              <Route path={PROTECTED_ROUTES.admin.applicant} element={<ApplicantFilePage />} />
              <Route path={PROTECTED_ROUTES.admin.people} element={<RegistrarRecordEntry><PeoplePage /></RegistrarRecordEntry>} />
              <Route path={PROTECTED_ROUTES.admin.person} element={<PersonRecordPage />} />
              <Route path={PROTECTED_ROUTES.admin.invitations} element={<RegistrarRecordEntry><InvitationsPage /></RegistrarRecordEntry>} />
              <Route path={PROTECTED_ROUTES.admin.invitation} element={<InvitationRecordPage />} />
              <Route path={PROTECTED_ROUTES.admin.courses} element={<RegistrarRecordEntry><CoursesPage /></RegistrarRecordEntry>} />
              <Route path={PROTECTED_ROUTES.admin.classroomIntake} element={<ClassroomIntakePage />} />
              <Route path={PROTECTED_ROUTES.admin.course} element={<RegistrarCoursePage />} />
              <Route path={PROTECTED_ROUTES.admin.academicStructure} element={<AcademicStructurePage />} />
              <Route path={PROTECTED_ROUTES.admin.courseHistory} element={<CourseHistoryPage />} />
              <Route path={`${PROTECTED_ROUTES.admin.historicalImports}/*`} element={<ImportsPage />} />
              <Route path={LEGACY_PROTECTED_ROUTES.admin.faculty} element={<RegistrarRecordEntry><LegacyFacultyLookup /></RegistrarRecordEntry>} />
            </Route>
            <Route path={PROTECTED_ROUTES.admin.inbox} element={<InboxPage shell="admin" />} />
            <Route path={PROTECTED_ROUTES.admin.settings} element={<SettingsPage />} />
            <Route path={PROTECTED_ROUTES.admin.integrations} element={<IntegrationsPage />} />
            <Route path={LEGACY_PROTECTED_ROUTES.admin.courseSetup} element={<RegistrarRecordEntry />} />
            <Route path={LEGACY_PROTECTED_ROUTES.admin.enrollment} element={<RegistrarRecordEntry />} />
            <Route path={LEGACY_PROTECTED_ROUTES.admin.attendance} element={<RegistrarRecordEntry />} />
            <Route path={LEGACY_PROTECTED_ROUTES.admin.assignments} element={<RegistrarRecordEntry />} />
            <Route path={LEGACY_PROTECTED_ROUTES.admin.grades} element={<RegistrarRecordEntry />} />
            <Route path={LEGACY_PROTECTED_ROUTES.admin.announcements} element={<RegistrarRecordEntry />} />
            <Route path={`${LEGACY_PROTECTED_ROUTES.admin.imports}/*`} element={<RegistrarRecordEntry />} />
            <Route path={LEGACY_PROTECTED_ROUTES.admin.students} element={<RegistrarRecordEntry />} />
            <Route path={LEGACY_PROTECTED_ROUTES.admin.accounts} element={<RegistrarRecordEntry />} />
          </Route>

          {/* The seat shells. Retired addresses stay routable only as
              compatibility redirects: SeatRecordEntry resolves each arrival
              once through seatRecordDestination and renders a page only where
              the arrival is itself a destination. */}
          <Route element={<Shell roles={["admin"]} workspace="teaching" />}>
            <Route path={PROTECTED_ROUTES.adminTeaching.drive} element={<CourseDrivePage />} />
            <Route path={PROTECTED_ROUTES.adminTeaching.dashboard} element={<InstructorDashboardPage basePath={PROTECTED_ROUTES.adminTeaching.dashboard} />} />
            <Route path={PROTECTED_ROUTES.adminTeaching.inbox} element={<InboxPage shell="adminTeaching" />} />
            <Route path={PROTECTED_ROUTES.adminTeaching.sessions} element={<SessionListPage workspace="adminTeaching" />} />
            <Route path={PROTECTED_ROUTES.adminTeaching.session} element={<SessionPortalPage shell="adminTeaching" />} />
            <Route path={PROTECTED_ROUTES.adminTeaching.course} element={<SeatRecordEntry shell="adminTeaching"><CourseWorkspaceEntry shell="adminTeaching" workspace="teaching" /></SeatRecordEntry>} />
            <Route path={PROTECTED_ROUTES.adminTeaching.courses} element={<SeatCoursesEntry shell="adminTeaching" />} />
            <Route path={LEGACY_PROTECTED_ROUTES.adminTeaching.coursePlan} element={<SeatRecordEntry shell="adminTeaching" />} />
            <Route path={LEGACY_PROTECTED_ROUTES.adminTeaching.courseMaterials} element={<SeatRecordEntry shell="adminTeaching" />} />
            <Route path={PROTECTED_ROUTES.adminTeaching.assignments} element={<SeatRecordEntry shell="adminTeaching" />} />
            <Route path={PROTECTED_ROUTES.adminTeaching.attendance} element={<SeatRecordEntry shell="adminTeaching" />} />
            <Route path={PROTECTED_ROUTES.adminTeaching.gradebook} element={<SeatRecordEntry shell="adminTeaching" />} />
            <Route path={PROTECTED_ROUTES.adminTeaching.announcements} element={<SeatRecordEntry shell="adminTeaching" />} />
          </Route>
          {/* Session portal (issue #321 Milestone 1). Three routes, ONE page.
              The route picks the surrounding workspace and the links out of it;
              the server rechecks tenant, membership, and the course
              relationship on every read, and an admin who does not teach this
              section gets the same nondisclosing refusal as a stranger. Every
              portal route is gated by the tenant release flag on the server,
              so these render only a refusal while it is off. */}
          <Route element={<Shell roles={["instructor"]} />}>
            <Route path={PROTECTED_ROUTES.instructor.dashboard} element={<InstructorDashboardPage />} />
            <Route path={PROTECTED_ROUTES.instructor.drive} element={<CourseDrivePage />} />
            <Route path={PROTECTED_ROUTES.instructor.inbox} element={<InboxPage shell="instructor" />} />
            <Route path={PROTECTED_ROUTES.instructor.sessions} element={<SessionListPage workspace="instructor" />} />
            <Route path={PROTECTED_ROUTES.instructor.session} element={<SessionPortalPage shell="instructor" />} />
            <Route path={PROTECTED_ROUTES.instructor.course} element={<SeatRecordEntry shell="instructor"><CourseWorkspaceEntry shell="instructor" workspace="role" /></SeatRecordEntry>} />
            <Route path={PROTECTED_ROUTES.instructor.courses} element={<SeatCoursesEntry shell="instructor" />} />
            <Route path={LEGACY_PROTECTED_ROUTES.instructor.coursePlan} element={<SeatRecordEntry shell="instructor" />} />
            <Route path={LEGACY_PROTECTED_ROUTES.instructor.courseMaterials} element={<SeatRecordEntry shell="instructor" />} />
            <Route path={PROTECTED_ROUTES.instructor.assignments} element={<SeatRecordEntry shell="instructor" />} />
            <Route path={PROTECTED_ROUTES.instructor.attendance} element={<SeatRecordEntry shell="instructor" />} />
            <Route path={PROTECTED_ROUTES.instructor.gradebook} element={<SeatRecordEntry shell="instructor" />} />
            <Route path={PROTECTED_ROUTES.instructor.announcements} element={<SeatRecordEntry shell="instructor" />} />
            <Route path={PROTECTED_ROUTES.instructor.faculty} element={<FacultyPage />} />
          </Route>
          <Route element={<Shell workspace="learning" />}>
            <Route path={PROTECTED_ROUTES.student.dashboard} element={<StudentDashboardPage />} />
            <Route path={PROTECTED_ROUTES.student.inbox} element={<InboxPage shell="student" />} />
            <Route path={PROTECTED_ROUTES.student.sessions} element={<SessionListPage workspace="student" />} />
            <Route path={PROTECTED_ROUTES.student.session} element={<SessionPortalPage shell="student" />} />
            <Route path={PROTECTED_ROUTES.student.course} element={<SeatRecordEntry shell="student"><CourseWorkspaceEntry shell="student" workspace="learning" /></SeatRecordEntry>} />
            <Route path={PROTECTED_ROUTES.student.courses} element={<SeatCoursesEntry shell="student" />} />
            <Route path={LEGACY_PROTECTED_ROUTES.student.coursePlan} element={<SeatRecordEntry shell="student" />} />
            <Route path={LEGACY_PROTECTED_ROUTES.student.courseMaterials} element={<SeatRecordEntry shell="student" />} />
            <Route path={PROTECTED_ROUTES.student.assignments} element={<SeatRecordEntry shell="student" />} />
            <Route path={PROTECTED_ROUTES.student.attendance} element={<SeatRecordEntry shell="student"><AttendancePage workspace="learning" /></SeatRecordEntry>} />
            <Route path={PROTECTED_ROUTES.student.grades} element={<SeatRecordEntry shell="student"><StudentGradesPage /></SeatRecordEntry>} />
            <Route path={PROTECTED_ROUTES.student.announcements} element={<SeatRecordEntry shell="student" />} />
            <Route path={PROTECTED_ROUTES.student.profile} element={<ProfilePage />} />
            <Route path={PROTECTED_ROUTES.student.faculty} element={<FacultyPage />} />
          </Route>

          {/* Auth-aware entry + catch-all */}
          <Route path="/" element={<RootEntry />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <Toaster richColors closeButton position="top-right" visibleToasts={3} />
      </BrowserRouter>
    </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
